// three.js preview of the cup: one mesh per part, orbit by drag. Adapted from apps/tray/src/preview3d.js.
// Core meshes are z-up with the logo facing -y; group.rotation.set(-PI/2, 0, 0) maps core (x, y, z) to
// three (x, z, -y), so the logo faces three's +z and the camera opens on +z (azimuth PI/2), 15 degrees up.
// setScene([{ name, mesh, color, pos?, explode? }]); setLift(k) moves every part by k * explode (the lid only).
// The framing is fixed per scene (the union of the lid at both ends), so lifting never moves the cup on screen.
import * as THREE from 'three';
const PHI_EDGE = 0.05, THETA0 = Math.PI / 2, PHI0 = Math.PI / 2 - (15 * Math.PI) / 180, FILL = 0.94;
// reserve() -> { top, bottom }: pixels at the canvas's top and bottom that overlays cover; the cup is framed in what is left.
export function createPreview(canvas, { reserve = () => ({ top: 0, bottom: 0 }) } = {}) {
  const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
  renderer.setPixelRatio(Math.min(devicePixelRatio, 2));
  const scene = new THREE.Scene();
  const camera = new THREE.PerspectiveCamera(35, 4 / 3, 1, 1000);
  let camR = 90, camTheta = THETA0, camPhi = PHI0, lift = 0;
  const placeCamera = () => {
    camera.position.set(camR * Math.sin(camPhi) * Math.cos(camTheta), camR * Math.cos(camPhi), camR * Math.sin(camPhi) * Math.sin(camTheta));
    camera.lookAt(0, 0, 0);
  };
  const key = new THREE.DirectionalLight(0xffffff, 1.4); key.position.set(-30, 40, 0); camera.add(key);
  const rim = new THREE.DirectionalLight(0xffffff, 0.9); rim.position.set(30, 10, -40); camera.add(rim);
  scene.add(new THREE.HemisphereLight(0xffffff, 0x888888, 1.2), camera);
  const group = new THREE.Group(); group.rotation.set(-Math.PI / 2, 0, 0); scene.add(group);
  const meshes = new Map();
  const render = () => renderer.render(scene, camera);
  const place = (m) => { const { pos, explode } = m.userData; m.position.set(pos[0] + lift * explode[0], pos[1] + lift * explode[1], pos[2] + lift * explode[2]); };
  const corners = (box) => {
    const pts = [];
    for (let i = 0; i < 8; i++) pts.push(new THREE.Vector3(i & 1 ? box.max.x : box.min.x, i & 2 ? box.max.y : box.min.y, i & 4 ? box.max.z : box.min.z));
    return pts;
  };
  function setScene(parts) {
    const geos = new Set();
    for (const c of [...group.children]) { group.remove(c); geos.add(c.geometry); c.material.dispose(); }
    for (const g of geos) g.dispose();
    meshes.clear();
    const bb = new THREE.Box3();
    for (const p of parts) {
      const geo = new THREE.BufferGeometry();
      geo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(p.mesh.vertProperties), p.mesh.numProp));
      geo.setIndex(new THREE.BufferAttribute(new Uint32Array(p.mesh.triVerts), 1));
      geo.computeVertexNormals(); geo.computeBoundingBox();
      const obj = new THREE.Mesh(geo, new THREE.MeshStandardMaterial({ color: p.color, roughness: 0.45, metalness: 0.05, flatShading: true }));
      const pos = p.pos || [0, 0, 0], explode = p.explode || [0, 0, 0];
      obj.userData = { pos, explode };
      for (const k of [0, 1]) {
        bb.union(geo.boundingBox.clone().translate(new THREE.Vector3(pos[0] + k * explode[0], pos[1] + k * explode[1], pos[2] + k * explode[2])));
      }
      place(obj); group.add(obj);
      if (p.name) meshes.set(p.name, obj);
    }
    const c = bb.getCenter(new THREE.Vector3());
    group.position.set(-c.x, -c.z, c.y);        // core centre of the union → the origin the camera looks at
    group.updateMatrixWorld(true);
    fit(); render();
  }
  // Back the camera off until the projected union of both lift ends fills FILL of the band between the overlays
  // (and of the width), then slide the frame with a view offset (tray's idiom) so the union is centred in that band.
  function fit() {
    const r = canvas.getBoundingClientRect();
    if (!r.width || !r.height) return;
    camera.aspect = r.width / r.height;
    const { top, bottom } = reserve();
    const yTop = 1 - (2 * top) / r.height, yBot = -1 + (2 * bottom) / r.height;      // the free band, in NDC
    const bandHalf = Math.max((yTop - yBot) / 2, 0.2), bandMid = (yTop + yBot) / 2;
    const saved = lift;
    const box = new THREE.Box3();
    for (const k of [0, 1]) { lift = k; for (const m of group.children) place(m); group.updateMatrixWorld(true); box.union(new THREE.Box3().setFromObject(group)); }
    lift = saved; for (const m of group.children) place(m); group.updateMatrixWorld(true);
    if (box.isEmpty()) return;
    const size = box.getSize(new THREE.Vector3());
    camR = Math.max(size.x, size.y, size.z, 10) * 3;
    let midY = 0;
    for (let i = 0; i < 5; i++) {
      camera.clearViewOffset();
      camera.far = Math.max(1000, camR * 4); camera.updateProjectionMatrix();
      placeCamera(); camera.updateMatrixWorld(true);
      let mx = 0, lo = Infinity, hi = -Infinity;
      for (const p of corners(box)) { const v = p.clone().project(camera); mx = Math.max(mx, Math.abs(v.x)); lo = Math.min(lo, v.y); hi = Math.max(hi, v.y); }
      midY = (lo + hi) / 2;
      camR *= Math.max(mx / FILL, (hi - lo) / 2 / (bandHalf * FILL));
    }
    camera.clearViewOffset(); camera.far = Math.max(1000, camR * 4); camera.updateProjectionMatrix();
    placeCamera(); camera.updateMatrixWorld(true);
    // the box's centre after the last distance change, then shifted onto the band's centre
    let lo = Infinity, hi = -Infinity;
    for (const p of corners(box)) { const v = p.clone().project(camera); lo = Math.min(lo, v.y); hi = Math.max(hi, v.y); }
    midY = (lo + hi) / 2;
    camera.setViewOffset(r.width, r.height, 0, ((bandMid - midY) * r.height) / 2, r.width, r.height);
  }
  function setLift(k) { lift = k; for (const m of group.children) place(m); group.updateMatrixWorld(true); render(); }
  // A part's bounding box projected to canvas pixels (relative to the canvas), null when it is not in the scene.
  function rectOf(name) {
    const m = meshes.get(name);
    if (!m) return null;
    group.updateMatrixWorld(true); camera.updateMatrixWorld(true);
    return project(new THREE.Box3().setFromObject(m));
  }
  function project(box) {
    const w = canvas.clientWidth, h = canvas.clientHeight;
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const p of corners(box)) {
      const v = p.clone().project(camera);
      const x = (v.x * 0.5 + 0.5) * w, y = (1 - (v.y * 0.5 + 0.5)) * h;
      minX = Math.min(minX, x); maxX = Math.max(maxX, x); minY = Math.min(minY, y); maxY = Math.max(maxY, y);
    }
    return { left: minX, top: minY, right: maxX, bottom: maxY };
  }
  // The whole scene's projected box at the current lift, canvas pixels.
  function sceneRect() {
    group.updateMatrixWorld(true); camera.updateMatrixWorld(true);
    const box = new THREE.Box3().setFromObject(group);
    if (box.isEmpty()) return null;
    return project(box);
  }
  function resize() {
    const w = canvas.clientWidth, h = canvas.clientHeight;
    if (!w || !h) return;
    renderer.setSize(w, h, false); fit(); render();
  }
  let dragging = false, lx = 0, ly = 0;
  canvas.addEventListener('pointerdown', (e) => { dragging = true; lx = e.clientX; ly = e.clientY; canvas.setPointerCapture(e.pointerId); });
  canvas.addEventListener('pointermove', (e) => {
    if (!dragging) return;
    camTheta -= (e.clientX - lx) * 0.008; camPhi = Math.min(Math.PI - PHI_EDGE, Math.max(PHI_EDGE, camPhi - (e.clientY - ly) * 0.006));
    lx = e.clientX; ly = e.clientY; placeCamera(); render();
  });
  canvas.addEventListener('pointerup', () => { dragging = false; });
  new ResizeObserver(resize).observe(canvas); resize();
  return { setScene, setLift, resize, refit: () => { fit(); render(); }, sceneRect, count: () => group.children.length, rectOf, canvasSize: () => ({ w: canvas.clientWidth, h: canvas.clientHeight }) };
}
